import React from "preact/compat";
import { h, Component } from 'preact';
import { connect } from "react-redux";
import reduce from "../reducers";
import * as actions from "../actions";
import util from "../util";
import { ArrowDownOutlined, ArrowLeftOutlined, ArrowRightOutlined, ArrowUpOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import text from "../canvas/text";
import AnimationHelpModal from "./AnimationHelpModal";

const defaultDuration = 20;

const animTypes = {
  fade: 'fade',
  wipeLeft: 'wipe-left',
  wipeRight: 'wipe-right',
  wipeUp: 'wipe-up',
  wipeDown: 'wipe-down',
  pop: 'pop',
  panLeft: 'pan-left',
  panRight: 'pan-right',

  wiggle: 'wiggle',
  rotate: 'rotate',
  pulse: 'pulse',
}

const animSpeeds = {
  'fade': 1,
  'wipe-left': 0.5,
  'wipe-right': 0.5,
  'wipe-up': 0.5,
  'wipe-down': 0.5,
  'pop': 0.4,
  'pan-left': 0.5,
  'pan-right': 0.5,
  'wiggle': 0.7,
  'rotate': 3,
  'pulse': 2,
}

const wipeAnimations = [animTypes.wipeLeft, animTypes.wipeRight, animTypes.wipeUp, animTypes.wipeDown];
const panAnimations = [animTypes.panLeft, animTypes.panRight];

function twoFrameAnimation(elemId, type, speed, duration, frame1, frame2) {
  if (type === 'start') {
    return `@keyframes ${elemId}-base {
      0% {
        ${frame1}
      }
      100% {
        ${frame2}
      }
    }`;
  } else if (type === 'end') {
    return `@keyframes ${elemId}-base {
      0% {
        ${frame2}
      }
      ${100 - (speed/duration)*100}% {
        ${frame2}
      }
      100% {
        ${frame1}
      }
    }`;
  } else if (type === 'both') {
    return `@keyframes ${elemId}-base {
      0% {
        ${frame1}
      }
      ${(speed/duration)*100}% {
        ${frame2}
      }
      ${100 - (speed/duration)*100}% {
        ${frame2}
      }
      100% {
        ${frame1}
      }
    }`;
  }
}

function twoFrameWipeAnimation(elemId, type, speed, duration, frame1, frame2, neutralFrame) {
  if (type === 'start') {
    return `@keyframes ${elemId}-base {
      0% {
        ${frame1}
      }
      100% {
        ${frame2}
      }
    }`;
  } else if (type === 'end') {
    return `@keyframes ${elemId}-base {
      0% {
        ${neutralFrame}
      }
      ${100 - (speed/duration)*100}% {
        ${frame2}
      }
      100% {
        ${frame1}
      }
    }`;
  } else if (type === 'both') {
    return `@keyframes ${elemId}-base {
      0% {
        ${frame1}
      }
      ${(speed/duration)*100}% {
        ${frame2}
      }
      ${(speed/duration)*100 + 1}% {
        ${neutralFrame}
      }
      ${100 - (speed/duration)*100 - 1}% {
        ${neutralFrame}
      }
      ${100 - (speed/duration)*100}% {
        ${frame2}
      }
      100% {
        ${frame1}
      }
    }`;
  }
}

function fadeAnimation(elemId, type, speed, duration) {
  return twoFrameAnimation(elemId, type, speed, duration, 'opacity: 0;', 'opacity: 1;');
}

function wipeLeftAnimation(elemId, type, speed, duration) {
  return twoFrameWipeAnimation(elemId, type, speed, duration, 'clip-path: polygon(100% 0, 100% 140%, 100% 140%, 100% 0);', 'clip-path: polygon(0 0, 0 140%, 100% 140%, 100% 0);', 'clip-path: none;');
}

function wipeRightAnimation(elemId, type, speed, duration) {
  return twoFrameWipeAnimation(elemId, type, speed, duration, 'clip-path: polygon(0 0, 0% 140%, 0% 140%, 0 0);', 'clip-path: polygon(0 0, 0 140%, 100% 140%, 100% 0);', 'clip-path: none;');
}

function wipeUpAnimation(elemId, type, speed, duration) {
  return twoFrameWipeAnimation(elemId, type, speed, duration, 'clip-path: polygon(0 100%, 0 140%, 100% 140%, 100% 140%);', 'clip-path: polygon(0 0, 0 140%, 100% 140%, 100% 0);', 'clip-path: none;');
}

function wipeDownAnimation(elemId, type, speed, duration) {
  return twoFrameWipeAnimation(elemId, type, speed, duration, 'clip-path: polygon(0 0, 0 0, 100% 0, 100% 0);', 'clip-path: polygon(0 0, 0 140%, 100% 140%, 100% 0);', 'clip-path: none;');
}

function popAnimation(elemId, type, speed, duration) {
  if (type === 'start') {
    return `@keyframes ${elemId}-base {
      0% {
        transform: scale(.5);
      }
      40% {
        transform: scale(1.2);
      }
      70% {
        transform: scale(0.9);
      }
      85% {
        transform: scale(1.1);
      }
      100% {
        transform: scale(1);
      }
    }`;
  } else {
    const finishIn = (speed/duration)*100;
    const startOut = 100 - (speed/duration)*100;
    if (type === 'end') {
      return `@keyframes ${elemId}-base {
        0% {
          transform: scale(1);
        }
        ${startOut}% {
          transform: scale(1);
        }
        ${startOut + finishIn*.4}% {
          transform: scale(1.1);
        }
        ${startOut + finishIn*.7}% {
          transform: scale(0.9);
        }
        ${startOut + finishIn*.85}% {
          transform: scale(1.2);
        }
        100% {
          transform: scale(.5);
        }
      }`;
    } else if (type === 'both') {
      return `@keyframes ${elemId}-base {
        0% {
          transform: scale(0);
        }
        ${finishIn*.4}% {
          transform: scale(1.2);
        }
        ${finishIn*.7}% {
          transform: scale(0.9);
        }
        ${finishIn*.85}% {
          transform: scale(1.1);
        }
        ${finishIn}% {
          transform: scale(1);
        }
        ${startOut}% {
          transform: scale(1);
        }
        ${startOut + finishIn*.4}% {
          transform: scale(1.1);
        }
        ${startOut + finishIn*.7}% {
          transform: scale(0.9);
        }
        ${startOut + finishIn*.85}% {
          transform: scale(1.2);
        }
        100% {
          transform: scale(0);
        }
      }`;
    }
  } 
}

function panRightAnimation(elemId, type, speed, duration) {
  return twoFrameAnimation(elemId, type, speed, duration, 'transform: translate(-100%);', 'transform: none;');
}

function panLeftAnimation(elemId, type, speed, duration) {
  return twoFrameAnimation(elemId, type, speed, duration, 'transform: translate(100%)', 'transform: none;');
}

const wiggleAnimation = (elemId) => `@keyframes ${elemId}-addon {
  0% {
    transform: translate(-5%);
  }
  50% {
    transform: translate(5%);
  }
  100% {
    transform: translate(-5%);
  }
}`;

const pulseAnimation = (elemId) => `@keyframes ${elemId}-addon {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}`;

const rotateAnimation = (elemId) => `@keyframes ${elemId}-addon {
  100% {
    transform: rotate(360deg);
  }
}`;

export default @connect(reduce, actions)
class TextGraphicAnimations extends Component {
  constructor() {
    super();

    this.state = {
      isHelpModal: false
    }

    this.openHelpModal = () => {
      this.setState({isHelpModal: true});
    }

    this.closeHelpModal = () => {
      this.setState({isHelpModal: false});
    }

    this.getAnimationObject = () => {
      const $element = this.props.$element;
      if (!$element) return;
      if (!util.isText($element) && !util.isFrame($element) && !util.isIllustration($element) && !util.isShape($element)) return;
      let objToAnimate = $element;

      if ($element.find('.ani-wrapper').length > 0) { 
        objToAnimate = $element.find('.ani-wrapper').first();
      } else {
        if (util.isText($element)) {
          objToAnimate = $element.find('.txt');
        } else if (util.isFrame($element)) {
          objToAnimate = $element.find('.img');
        } else if (util.isIllustration($element) || util.isShape($element)) {
          objToAnimate = $element.find('.svg-content');
        }
      }
      return objToAnimate;
    }

    this.fixElId = ($element) => {
      let id = $element.attr('id');
      if (!isNaN(parseInt(id.charAt(0)))) {
        const newId = util.UUID.get();
        $element.attr('id', newId);
        id = newId;
      }
      return id;
    }

    this.animate = (animationType, elId, animationId, speed, duration = defaultDuration, startEnd = 'start') => {
      const $element = this.props.$element;

      let keyFrames = '';
      let animationCSS = '';
      switch (animationType) {
        case animTypes.fade: {
          keyFrames = fadeAnimation(elId, startEnd, speed, duration);
          animationCSS = `${animationId} ${startEnd === 'start' ? speed : duration}s linear ${startEnd !== 'start' ? 'infinite' : 1}`;
          break;
        }
        case animTypes.wipeLeft: {
          keyFrames = wipeLeftAnimation(elId, startEnd, speed, duration);
          animationCSS = `${animationId} ${startEnd === 'start' ? speed : duration}s linear ${startEnd !== 'start' ? 'infinite' : 1}`;
          break;
        }
        case animTypes.wipeRight: {
          keyFrames = wipeRightAnimation(elId, startEnd, speed, duration);
          animationCSS = `${animationId} ${startEnd === 'start' ? speed : duration}s linear ${startEnd !== 'start' ? 'infinite' : 1}`;
          break;
        }
        case animTypes.wipeUp: {
          keyFrames = wipeUpAnimation(elId, startEnd, speed, duration);
          animationCSS = `${animationId} ${startEnd === 'start' ? speed : duration}s linear ${startEnd !== 'start' ? 'infinite' : 1}`;
          break;
        }
        case animTypes.wipeDown: {
          keyFrames = wipeDownAnimation(elId, startEnd, speed, duration);
          animationCSS = `${animationId} ${startEnd === 'start' ? speed : duration}s linear ${startEnd !== 'start' ? 'infinite' : 1}`;
          break;
        }
        case animTypes.pop: {
          keyFrames = popAnimation(elId, startEnd, speed, duration);
          animationCSS = `${animationId} ${startEnd === 'start' ? speed : duration}s ease-in-out ${startEnd !== 'start' ? 'infinite' : 1}`;
          break;
        }
        case animTypes.panLeft: {
          keyFrames = panLeftAnimation(elId, startEnd, speed, duration);
          animationCSS = `${animationId} ${startEnd === 'start' ? speed : duration}s linear ${startEnd !== 'start' ? 'infinite' : 1}`;
          break;
        }
        case animTypes.panRight: {
          keyFrames = panRightAnimation(elId, startEnd, speed, duration);
          animationCSS = `${animationId} ${startEnd === 'start' ? speed : duration}s linear ${startEnd !== 'start' ? 'infinite' : 1}`;
          break;
        }

        case animTypes.wiggle: {
          keyFrames = wiggleAnimation(elId);
          animationCSS = `${animationId} ${speed}s ease-in-out infinite`;
          break;
        }
        case animTypes.pulse: {
          keyFrames = pulseAnimation(elId);
          animationCSS = `${animationId} ${speed}s ease-in-out infinite`;
          break;
        }
        case animTypes.rotate: {
          keyFrames = rotateAnimation(elId);
          animationCSS = `${animationId} ${speed}s linear infinite`;
          break;
        }
      }

      $element.prepend(`<style class="${animationId}">` + keyFrames + '</style>');
      const $outerAniObj = this.getAnimationObject();
      $outerAniObj.wrap(`<div class="ani-wrapper ${animationId}" style="width: 100%; height: 100%; animation: ${animationCSS};"></div>`);

      if (util.isText($element)) {
        text.removeQuill($element);
        text.initQuill($element);
      }
      this.props.updateElementAttribute('isAnimated', true);
    }

    this.baseAnimate = (animationType, startEnd) => {
      const $element = this.props.$element;
      if (!$element) return;
      const elId = this.fixElId($element);
      const animationId = `${elId}-base`;
      const curStyleObj = $element.find(`style.${animationId}`);
      if (curStyleObj.length > 0) {
        curStyleObj.remove();
      }
      const curWrapperObj = $element.find(`.ani-wrapper.${animationId}`);
      if (curWrapperObj.length > 0) {
        curWrapperObj.replaceWith(curWrapperObj.html());
      }

      $element.attr('data-base-animation', animationType);
      this.props.updateElementAttribute('baseAnimation', animationType);
      $element.attr('data-anim-start-end', startEnd);
      this.props.updateElementAttribute('animationStartOrEnd', startEnd);

      let speed = 0;
      if ($element.attr('data-base-anim-speed')) {
        speed = parseFloat($element.attr('data-base-anim-speed'));
      } else {
        speed = animSpeeds[animationType];
        $element.attr('data-base-anim-speed', speed);
        this.props.updateElementAttribute('baseSpeed', speed);
      }
      const duration = $element.attr('data-base-anim-duration') ? $element.attr('data-base-anim-duration') : defaultDuration;
      this.animate(animationType, elId, animationId, speed, duration, startEnd);
    }

    this.addOnAnimate = (animationType, speed) => {
      const $element = this.props.$element;
      if (!$element) return;
      const elId = this.fixElId($element);
      const animationId = `${elId}-addon`;
      const curStyleObj = $element.find(`style.${animationId}`);
      if (curStyleObj.length > 0) {
        curStyleObj.remove();
      }
      const curWrapperObj = $element.find(`.ani-wrapper.${animationId}`);
      if (curWrapperObj.length > 0) {
        curWrapperObj.replaceWith(curWrapperObj.html());
      }
      $element.attr('data-addon-animation', animationType);
      this.props.updateElementAttribute('addOnAnimation', animationType);
      this.animate(animationType, elId, animationId, speed);
    }

    this.removeAnimation = (animationId) => {
      const $element = this.props.$element;
      if (!$element) return;

      const curStyleObj = $element.find(`style.${animationId}`);
      if (curStyleObj.length > 0) {
        curStyleObj.remove();
      }
      const curWrapperObj = $element.find(`.ani-wrapper.${animationId}`);
      if (curWrapperObj.length > 0) {
        curWrapperObj.replaceWith(curWrapperObj.html());
      }

      if (util.isText($element)) {
        text.removeQuill($element);
        text.initQuill($element);
      }
    }

    this.onBaseAnimationClick = (animationType) => {
      const $element = this.props.$element;
      if (!$element) return;

      const activeAnimation = $element.attr('data-base-animation');
      let untoggleAnimation = activeAnimation === animationType;
      if (wipeAnimations.includes(activeAnimation) && wipeAnimations.includes(animationType)) {
        untoggleAnimation = true;
      }
      if (panAnimations.includes(activeAnimation) && panAnimations.includes(animationType)) {
        untoggleAnimation = true
      }
      if (untoggleAnimation) {
        const elId = $element.attr('id');
        const animationId = `${elId}-base`;
        this.removeAnimation(animationId);
        $element.attr('data-base-animation', '');
        this.props.updateElementAttribute('baseAnimation', '');
      } else {
        this.baseAnimate(animationType, this.props.animationStartOrEnd || 'start');
      }
    }

    this.onAddOnAnimationClick = (animationType) => {
      const $element = this.props.$element;
      if (!$element) return;

      const activeAnimation = $element.attr('data-addon-animation');
      if (activeAnimation === animationType) {
        const elId = $element.attr('id');
        const animationId = `${elId}-addon`;
        this.removeAnimation(animationId);
        $element.attr('data-addon-animation', '');
        this.props.updateElementAttribute('addOnAnimation', '');
      } else {
        let speed = 0;
        if ($element.attr('data-addon-anim-speed')) {
          speed = parseFloat($element.attr('data-addon-anim-speed'));
        } else {
          speed = animSpeeds[animationType];
          $element.attr('data-addon-anim-speed', speed);
          this.props.updateElementAttribute('addOnSpeed', speed);
        }
        this.addOnAnimate(animationType, speed);
      }
    }

    this.updateSpeed = (ev, animationCat) => {
      const $element = this.props.$element;
      if (!$element) return;
      const oldSpeed = parseFloat($element.attr(`data-${animationCat}-anim-speed`));
      const newSpeed = parseFloat(ev.target.value);
      if (oldSpeed === newSpeed) return;
      $element.attr(`data-${animationCat}-anim-speed`, newSpeed);

      if (animationCat === 'base') {
        this.props.updateElementAttribute('baseSpeed', newSpeed);
        $element.attr('data-base-anim-speed', newSpeed);
        this.baseAnimate($element.attr('data-base-animation'), this.props.animationStartOrEnd || 'start');
      } else if (animationCat === 'addon') {
        this.props.updateElementAttribute('addOnSpeed', newSpeed);
        $element.attr('data-addon-anim-speed', newSpeed);
        this.addOnAnimate($element.attr('data-addon-animation'), newSpeed);
      }
    }

    this.updateDuration = (ev) => {
      const $element = this.props.$element;
      if (!$element) return;
      const oldDuration = parseInt($element.attr('data-base-anim-duration'));
      const newDuration = parseInt(ev.target.value);
      if (oldDuration === newDuration) return;
      $element.attr('data-base-anim-duration', newDuration);
      this.props.updateElementAttribute('baseDuration', newDuration);
      this.baseAnimate($element.attr('data-base-animation'), this.props.animationStartOrEnd || 'start');
    }

    this.speedSettings = (mode, currentSpeed, currentDuration) => {
      return <div>
      <ul className="slider-list">
        <li className="slider-item">
          <label className="font-spacing">Speed</label>
          <div className="toolbar-wrap" style="transform: rotate(180deg);">
            <input className="slider" type="range" min=".1" max="5" step=".1"
              value={currentSpeed} onChange={(ev) => this.updateSpeed(ev, mode)} />
          </div>
        </li>
        {mode === 'base' && this.props.animationStartOrEnd !== 'start' &&
        <li className="slider-item">
          <label className="font-spacing">Duration</label>
          <div className="toolbar-wrap">
            <input className="slider" type="range" min="20" max="120" step="1"
              value={currentDuration} onChange={(ev) => this.updateDuration(ev, mode)} />
          </div>
          <div class="value-label">{currentDuration}s</div>
        </li>}
      </ul>
      {mode === 'base' && <label className={"button-label with-help-btn"}>
        <span>Animate</span>
        <div className={'help-btn hint--bottom'} data-hint="What's this?" onClick={() => this.openHelpModal()}><QuestionCircleOutlined/></div>
      </label>}
      {mode === 'base' && 
      <ul className="start-end-list">
        <li className={this.props.animationStartOrEnd === 'both' ? 'active' : ''} onClick={() => this.baseAnimate(this.props.baseAnimation, 'both')}>
          Both
        </li>
        <li className={this.props.animationStartOrEnd === 'start' ? 'active' : ''} onClick={() => this.baseAnimate(this.props.baseAnimation, 'start')}>
          On Enter
        </li>
        <li className={this.props.animationStartOrEnd === 'end' ? 'active' : ''} onClick={() => this.baseAnimate(this.props.baseAnimation, 'end')}>
          On Exit
        </li>
      </ul>}
      {mode === 'base' && [...wipeAnimations, ...panAnimations].includes(this.props.baseAnimation) &&
      <label className={"button-label"}>Direction</label>}
      {mode === 'base' && panAnimations.includes(this.props.baseAnimation) &&
      <ul className="start-end-list direction-list">
        <li className={this.props.baseAnimation === animTypes.panLeft ? 'active' : ''} onClick={() => this.baseAnimate(animTypes.panLeft, this.props.animationStartOrEnd)}>
          <ArrowLeftOutlined/>
        </li>
        <li className={this.props.baseAnimation === animTypes.panRight ? 'active' : ''} onClick={() => this.baseAnimate(animTypes.panRight, this.props.animationStartOrEnd)}>
          <ArrowRightOutlined/>
        </li>
      </ul>}
      {mode === 'base' && wipeAnimations.includes(this.props.baseAnimation) &&
      <ul className="start-end-list direction-list">
        <li className={this.props.baseAnimation === animTypes.wipeLeft ? 'active' : ''} onClick={() => this.baseAnimate(animTypes.wipeLeft, this.props.animationStartOrEnd)}>
          <ArrowLeftOutlined/>
        </li>
        <li className={this.props.baseAnimation === animTypes.wipeRight ? 'active' : ''} onClick={() => this.baseAnimate(animTypes.wipeRight, this.props.animationStartOrEnd)}>
          <ArrowRightOutlined/>
        </li>
        <li className={this.props.baseAnimation === animTypes.wipeUp ? 'active' : ''} onClick={() => this.baseAnimate(animTypes.wipeUp, this.props.animationStartOrEnd)}>
          <ArrowUpOutlined/>
        </li>
        <li className={this.props.baseAnimation === animTypes.wipeDown ? 'active' : ''} onClick={() => this.baseAnimate(animTypes.wipeDown, this.props.animationStartOrEnd)}>
          <ArrowDownOutlined/>
        </li>
      </ul>}
    </div>
    }
  }

  render(props, state) {
    return (<div class="animation-options">
      {!props.isMobileVersion && <div className="title">Animate</div>}
      <ul class="animate">
        <li className={props.baseAnimation === animTypes.fade ? 'act' : ''} onClick={() => this.onBaseAnimationClick(animTypes.fade)}>
          <img src="https://timber.mhmcdn.com/site/timber/animations/Fade.svg" alt="animation"/>
          <div className={'label'}>Fade</div>
        </li>
        <li className={wipeAnimations.includes(props.baseAnimation) ? 'act' : ''} onClick={() => this.onBaseAnimationClick(animTypes.wipeRight)}>
          <img src="https://timber.mhmcdn.com/site/timber/animations/Wipe.svg" alt="animation"/>
          <div className={'label'}>Wipe</div>
        </li>
        <li className={props.baseAnimation === animTypes.pop ? 'act' : ''} onClick={() => this.onBaseAnimationClick(animTypes.pop)}>
          <img src="https://timber.mhmcdn.com/site/timber/animations/Pop.svg" alt="animation"/>
          <div className={'label'}>Pop</div>
        </li>
        <div className={`animation-settings ${[animTypes.fade, ...wipeAnimations, animTypes.pop].includes(props.baseAnimation) ? 'active' : ''}`}>
          {this.speedSettings('base', props.baseSpeed, props.baseDuration)}
        </div>
        <li className={panAnimations.includes(props.baseAnimation) ? 'act' : ''} onClick={() => this.onBaseAnimationClick(animTypes.panRight)}>
          <img src="https://timber.mhmcdn.com/site/timber/animations/Pan.svg" alt="animation"/>
          <div className={'label'}>Pan</div>
        </li>
        <div className={`animation-settings ${panAnimations.includes(props.baseAnimation) ? 'active' : ''}`}>
          {this.speedSettings('base', props.baseSpeed, props.baseDuration)}
        </div>
      </ul>

      <div style="margin-top: 32px;" className="title">Add-On Effects</div>
      <div className="separator" />
      <ul class="add-ons">
        <li className={props.addOnAnimation === animTypes.wiggle ? 'act' : ''} onClick={() => this.onAddOnAnimationClick(animTypes.wiggle)}>
          <img src="https://timber.mhmcdn.com/site/timber/animations/Wiggle.svg" alt="animation"/>
          <div className={'label'}>Wiggle</div>
        </li>
        <li className={props.addOnAnimation === animTypes.pulse ? 'act' : ''} onClick={() => this.onAddOnAnimationClick(animTypes.pulse)}>
          <img src="https://timber.mhmcdn.com/site/timber/animations/Pulse.svg" alt="animation"/>
          <div className={'label'}>Pulse</div>
        </li>
        <li className={props.addOnAnimation === animTypes.rotate ? 'act' : ''} onClick={() => this.onAddOnAnimationClick(animTypes.rotate)}>
          <img src="https://timber.mhmcdn.com/site/timber/animations/Rotate.svg" alt="animation"/>
          <div className={'label'}>Rotate</div>
        </li>
        <div className={`animation-settings ${[animTypes.wiggle, animTypes.pulse, animTypes.rotate].includes(props.addOnAnimation) ? 'active' : ''}`}>
          {this.speedSettings('addon', props.addOnSpeed)}
        </div>
      </ul>

      <AnimationHelpModal open={state.isHelpModal} onClose={() => this.closeHelpModal()} isMobile={props.isMobileVersion} />
    </div>)
  }
}
